import type { BuildState } from '@/types';
import { parseIntSafe, parseFloatSafe, RANGES } from './validate';
import { SparrowError } from './errors';

/**
 * Public shareable build permalinks.
 *
 * Encodes a BuildState into a URL-safe base64 string (with gzip-style
 * minimal field names) so it fits in a URL hash. Anyone opening the link
 * sees the exact arrow build pre-loaded — drives shares + return traffic.
 *
 * URL format:  https://sparrowexpeditions.com/tools/arrow-builder#b=<encoded>
 *
 * Free-tier users can share builds (the URL itself is the "save"); Pro users
 * get cloud-synced builds + named history.
 */

const HASH_PREFIX = '#b=';

/**
 * Encode a build state into a URL hash fragment.
 * Minimizes the payload by using single-letter field names.
 */
export function encodeBuild(state: BuildState): string {
  const compact = {
    n: state.name,
    s: state.shaftIdx,
    sp: state.spineKey,
    l: state.length,
    pp: state.pointPreset,
    pw: state.pointWeight,
    i: state.insertIdx,
    nk: state.nockIdx,
    v: state.vaneIdx,
    vc: state.vaneCount,
    w: state.wrap ? 1 : 0,
    wl: state.wrapLen,
    vbp: state.vaneBackPosition,
    f: state.fps,
    b: {
      t: state.bow.type,
      dw: state.bow.drawWeight,
      dl: state.bow.drawLength,
      ib: state.bow.iboSpeed,
      lo: state.bow.letOff,
    },
    c: state.ctiPosition,
    cf: state.ctiFrontLength,
    cfw: state.ctiFrontWeight,
    cb: state.ctiBackLength,
    cbw: state.ctiBackWeight,
  };
  const json = JSON.stringify(compact);
  // Base64 URL-safe (no padding, +/= → -_)
  return urlSafeBase64Encode(json);
}

/**
 * Decode a URL hash fragment into a BuildState (or null if invalid).
 * All numeric fields are clamped via RANGES — invalid encoded values silently
 * collapse to safe defaults rather than producing NaN downstream.
 */
export function decodeBuild(encoded: string): BuildState | null {
  try {
    const json = urlSafeBase64Decode(encoded);
    const c = JSON.parse(json) as Record<string, unknown> & { b?: Record<string, unknown> };
    if (typeof c !== 'object' || c === null) return null;
    const b = c.b ?? {};
    const bowType: 'compound' | 'recurve' | 'traditional' =
      b.t === 'recurve' || b.t === 'traditional' ? b.t : 'compound';
    const vaneCount = Math.max(3, Math.min(5, Math.round(Number(c.vc) || 3))) as 3 | 4 | 5;
    return {
      name: typeof c.n === 'string' ? c.n.slice(0, 80) : 'Shared build',
      shaftIdx: parseIntSafe(c.s, 0, 0, 10000),
      spineKey: parseIntSafe(c.sp, 340, 50, 2000),
      length: parseFloatSafe(c.l, RANGES.arrowLength.default, RANGES.arrowLength.min, RANGES.arrowLength.max),
      pointPreset: parseIntSafe(c.pp, 0, 0, 100),
      pointWeight: parseIntSafe(c.pw, RANGES.pointWeight.default, RANGES.pointWeight.min, RANGES.pointWeight.max),
      insertIdx: parseIntSafe(c.i, 0, 0, 10000),
      nockIdx: parseIntSafe(c.nk, 0, 0, 10000),
      vaneIdx: parseIntSafe(c.v, 0, 0, 10000),
      vaneCount,
      wrap: c.w === 1,
      wrapLen: parseFloatSafe(c.wl, RANGES.wrapLen.default, RANGES.wrapLen.min, RANGES.wrapLen.max),
      vaneBackPosition: parseFloatSafe(c.vbp, RANGES.vaneBackPosition.default, RANGES.vaneBackPosition.min, RANGES.vaneBackPosition.max),
      fps: parseFloatSafe(c.f, RANGES.fps.default, RANGES.fps.min, RANGES.fps.max),
      bow: {
        type: bowType,
        drawWeight: parseFloatSafe(b.dw, RANGES.drawWeight.default, RANGES.drawWeight.min, RANGES.drawWeight.max),
        drawLength: parseFloatSafe(b.dl, RANGES.drawLength.default, RANGES.drawLength.min, RANGES.drawLength.max),
        iboSpeed: parseIntSafe(b.ib, RANGES.iboSpeed.default, RANGES.iboSpeed.min, RANGES.iboSpeed.max),
        letOff: parseIntSafe(b.lo, RANGES.letOff.default, RANGES.letOff.min, RANGES.letOff.max),
      },
      ctiPosition: (c.c === 'front' || c.c === 'both') ? c.c : 'none',
      ctiFrontLength: parseFloatSafe(c.cf, 0, RANGES.ctiLength.min, RANGES.ctiLength.max),
      ctiFrontWeight: parseFloatSafe(c.cfw, 0, RANGES.ctiWeight.min, RANGES.ctiWeight.max),
      ctiBackLength: parseFloatSafe(c.cb, 0, RANGES.ctiLength.min, RANGES.ctiLength.max),
      ctiBackWeight: parseFloatSafe(c.cbw, 0, RANGES.ctiWeight.min, RANGES.ctiWeight.max),
    };
  } catch {
    return null;
  }
}

/**
 * Build a full shareable URL from the current build state.
 * Uses window.location.origin + pathname so it works whether deployed on
 * Shopify, Netlify, or as a standalone file.
 */
export function buildShareUrl(state: BuildState): string {
  if (typeof window === 'undefined') return '';
  const encoded = encodeBuild(state);
  const baseUrl = `${window.location.origin}${window.location.pathname}`;
  return `${baseUrl}${HASH_PREFIX}${encoded}`;
}

/**
 * Try to read a build state from the current URL hash.
 * Returns null if no encoded build is present or decoding fails.
 */
export function readBuildFromHash(): BuildState | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash.startsWith(HASH_PREFIX)) return null;
  return decodeBuild(hash.slice(HASH_PREFIX.length));
}

/**
 * Copy the share URL to clipboard. Returns the URL on success.
 * Throws SparrowError('clipboard_denied' | 'clipboard_unsupported') on failure
 * so caller can show the right message + a manual-copy fallback.
 */
export async function copyShareUrl(state: BuildState): Promise<string> {
  const url = buildShareUrl(state);
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    throw new SparrowError('clipboard_unsupported');
  }
  try {
    await navigator.clipboard.writeText(url);
    return url;
  } catch (err) {
    throw new SparrowError('clipboard_denied', undefined, err);
  }
}

// ────────────────────────────────────────────
// URL-safe base64 (RFC 4648 §5) — handles UTF-8 cleanly
// ────────────────────────────────────────────
function urlSafeBase64Encode(s: string): string {
  // Encode UTF-8 first to handle any non-ASCII characters in names
  const utf8 = unescape(encodeURIComponent(s));
  const base64 = btoa(utf8);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function urlSafeBase64Decode(s: string): string {
  const base64 = s.replace(/-/g, '+').replace(/_/g, '/');
  // Restore padding
  const padded = base64 + '==='.slice((base64.length + 3) % 4);
  const utf8 = atob(padded);
  return decodeURIComponent(escape(utf8));
}
